import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import CurriculumBuilder from '../components/curriculum/CurriculumBuilder';
import PageTemplate from './PageTemplate';
import { buildCourseRequestData } from '../utils/courseFormUtils';
import './ModulePage.css';

const initialForm = {
  course_name: '',
  course_fee: '',
  original_price: '',
  tag: '',
  description: '',
  duration: '',
  level: 'Beginner',
  thumbnail: '',
};

function formatDecimalFee(fee) {
  const num = Number(fee);
  if (Number.isNaN(num)) {
    return fee;
  }
  return num.toFixed(2);
}

function buildCoursePayload(
  form,
  trimmedName,
  formattedFee,
  formattedOriginal,
  curriculumOutline
) {
  return {
    course_name: trimmedName,
    course_fee: formattedFee,
    original_price: formattedOriginal,
    tag: form.tag?.trim() || '',
    description: form.description?.trim() || '',
    duration: form.duration?.trim() || '',
    level: form.level?.trim() || 'Beginner',
    thumbnail: form.thumbnail?.trim() || '',
    curriculum_outline: curriculumOutline,
  };
}

function firstApiError(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.detail && typeof data.detail === 'string') return data.detail;
  if (data.message && typeof data.message === 'string') return data.message;
  for (const val of Object.values(data)) {
    if (Array.isArray(val) && val[0]) return `${val[0]}`;
    if (typeof val === 'string') return val;
  }
  return null;
}

function AddCourse() {
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [curriculumOutline, setCurriculumOutline] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedName = form.course_name.trim();
    const trimmedFee = form.course_fee.toString().trim();

    if (!trimmedName || !trimmedFee) {
      setError('Please enter course name and course fee.');
      return;
    }

    const numericFee = Number(trimmedFee);
    if (Number.isNaN(numericFee) || numericFee <= 0) {
      setError('Please enter a valid course fee (greater than 0).');
      return;
    }

    const origStr = form.original_price?.toString().trim();
    if (!origStr) {
      setError('Please enter original price.');
      return;
    }

    const numericOriginal = Number(origStr);
    if (Number.isNaN(numericOriginal) || numericOriginal < 0) {
      setError('Please enter a valid original price (0 or greater).');
      return;
    }

    const formattedFee = formatDecimalFee(trimmedFee);
    const formattedOriginal = formatDecimalFee(origStr);
    setSubmitting(true);

    try {
      const body = buildCourseRequestData(
        buildCoursePayload(
          form,
          trimmedName,
          formattedFee,
          formattedOriginal,
          curriculumOutline
        )
      );
      const res = await apiClient.post('/api/courses/', body);

      const msg =
        res?.data?.message ||
        (res?.data?.course ? 'Course created successfully.' : null) ||
        'Course added successfully.';
      setSuccess(msg);
      setForm(initialForm);
      setCurriculumOutline([]);
      setTimeout(() => navigate('/courses', { replace: true }), 600);
    } catch (err) {
      console.error('Course creation error:', err);
      const errorData = err?.response?.data;
      const errorMsg =
        firstApiError(errorData) ||
        err?.message ||
        'Unable to add course. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageTemplate title="Add Course" subtitle="Create a new course">
      <div className="add-teacher-panel">
        <div className="add-teacher-header">
          <div>
            <h2>Course details</h2>
            <p>
              Enter course details, then build the curriculum outline with sections and topics.
            </p>
          </div>
          <span className="add-teacher-badge">POST /api/courses/</span>
        </div>

        <form className="add-teacher-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="course_name">Course name</label>
            <input
              id="course_name"
              name="course_name"
              type="text"
              placeholder="e.g. Full Stack Web Development"
              value={form.course_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="course_fee">Course fee</label>
            <input
              id="course_fee"
              name="course_fee"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 2500"
              value={form.course_fee}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="original_price">Original price</label>
            <input
              id="original_price"
              name="original_price"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 3750"
              value={form.original_price}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="tag">Tag</label>
            <input
              id="tag"
              name="tag"
              type="text"
              placeholder="e.g. 🟢"
              value={form.tag}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="thumbnail">Thumbnail</label>
            <input
              id="thumbnail"
              name="thumbnail"
              type="text"
              placeholder="e.g. 🌐 or image URL"
              value={form.thumbnail}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="duration">Duration</label>
            <input
              id="duration"
              name="duration"
              type="text"
              placeholder="e.g. 3 months"
              value={form.duration}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="level">Level</label>
            <select
              id="level"
              name="level"
              className="select-input"
              value={form.level}
              onChange={handleChange}
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>

          <div className="form-group form-group-wide">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              placeholder="Brief description of the course."
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className="form-group form-group-wide curriculum-slot">
            <CurriculumBuilder
              courseTitle={form.course_name}
              value={curriculumOutline}
              onChange={setCurriculumOutline}
            />
          </div>

          {error && <p className="form-error form-group-wide">{error}</p>}
          {success && <p className="form-success form-group-wide">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/courses')}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Course'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default AddCourse;
